/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import router from "next/router";

import {
  getOnboardingQuestions,
  getAllIndianCities,
  postOnboardingQuestions,
  getOnboardingAnswers
} from "api";

import {handleNewApiError} from "helpers";

import OnboardingQuestions from "./OnboardingQuestions";

import {triggerToast} from "helpers";

const propTypes = {
  pushUrlDomain: PropTypes.string.isRequired,
  step: PropTypes.number.isRequired,
  updateStep: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired
};

const OnboardingQuestionsContainer = ({
  step,
  updateStep,
  userData,
  pushUrlDomain
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({});
  const [indianCityList, setIndianCityList] = useState([]);
  const [isCityRequired, setIsCityRequired] = useState(false);
  const [isFetchingQuestions, setIsFetchingQuestions] = useState(true);
  const [isUpdatingAnswer, setIsUpdatingAnswer] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [metaData, setMetaData] = useState({});

  const isDisabled = questions.length === Object.keys(formData).length;
  const isOptional = questions.some(eachQuestion => eachQuestion.optional === true);
  const isNextStagePresent = metaData?.next && metaData.next.length > 0;
  const isPreviousStagePresent = metaData?.prev && metaData.prev.length > 0;


  useEffect(() => {
    getQuestions();
  }, []);


  useEffect(() => {
    if (questions.length > 0) {
      setIsCityRequired(questions.filter((eachQuestion) => eachQuestion.question.qtype.includes("city")));
      const obj = {};
      questions.map((question) => {
        if (question.selectedAnswer) {
          obj[question.externalId] = question.selectedAnswer;
        }
      });
      console.log(obj, "obj");
      setFormData(obj);
      console.log(Object.keys(obj)?.length, " settted from here");
    }
  }, [questions]);

  useEffect(() => {
    if (!isFetchingQuestions) {
      checkIsQuestionPresent();
    }
  }, [isFetchingQuestions]);

  useEffect(() => {
    if (isCityRequired.length > 0) {
      (async function () {
        try {
          setIsLoading(true);
          const apiKey = window.sessionStorage.getItem("apiKey");
          const res = await getAllIndianCities(apiKey);
          setIndianCityList(res.data.cities);
        } catch (error) {
          handleNewApiError(error);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [isCityRequired]);

  useEffect(() => {
    if (metaData && metaData.level === 0) {
      setShowBackButton(false);
    } else {
      setShowBackButton(true);
    }
  }, [metaData]);

  useEffect(() => {
    if (questions.length > 0 && isUpdatingAnswer) {
      updateFormData(questions);
    }
  }, [questions, isUpdatingAnswer]);

  const disableHandler = () => {
    if (isOptional) {
      return !isOptional;
    }
    if (!isNextStagePresent && !isOptional) {
      const isDisable =  Object.values(formData).some(data => data?.answer?.length > 0);
      return !isDisable;
    }
    return !isDisabled;
  };

  const updateFormData = (questions) => {
    console.log("triggered backward flow");
    const updatedFormData = {};

    questions.forEach((question) => {
      const {options, multiSelect, selectedOptions} = question;
      // handling diffrent types of questions
      if (options?.length > 0) {
        if (!multiSelect) {
          // cases : Select Answer Question (Single Select)
          const selectedOption = Array.isArray(selectedOptions) && selectedOptions.length > 0 ? selectedOptions[0] : null;
          updatedFormData[question.externalId] = {
            answer: selectedOption ? selectedOption.externalId : "",
            qnaStage: question.externalId,
            displayTitle: selectedOption ? selectedOption.answer : ""
          };
        } else {
          // cases : Select MultiSelect Answer Question
          updatedFormData[question.externalId] = {
            answer: Array.isArray(selectedOptions) ? selectedOptions.map((option) => option.externalId) : [],
            qnaStage: question.externalId,
            displayTitle: Array.isArray(selectedOptions) ? selectedOptions.map((option) => option.answer) : []
          };
        }
      } else {
        // cases : Custom Answer Question
        updatedFormData[question.externalId] = {
          qnaStage: question.externalId,
          customAnswer: question.customAnswer,
          displayTitle: question.customAnswer
        };
      }
    });

    setFormData(updatedFormData);
  };


  const checkIsQuestionPresent = () => {
    if (!questions.length > 0 && !isFetchingQuestions) {
      updateStep(4);
    } else {
      updateStep(3);
    }
  };

  const getQuestions = async (stageToBeNavigated) => {
    try {
      setIsLoading(true);
      setIsFetchingQuestions(true);
      const apiKey = window.sessionStorage.getItem("apiKey");
      const response = await getOnboardingQuestions(apiKey, stageToBeNavigated);
      setMetaData(response.data.meta);
      setQuestions(response.data.questions);
      checkIsQuestionPresent();
      setIsLoading(false);
      setIsFetchingQuestions(false);
    } catch (error) {
      handleNewApiError(error);
      setIsLoading(false);
      setIsFetchingQuestions(false);
    }
  };


  const handleGoBack = () => {
    if (isPreviousStagePresent) {
      getQuestions(metaData.prev);
      setIsUpdatingAnswer(true);
    } else {
      updateStep(2);
    }
  };

  const handleNextQuestions = () => {
    if (isNextStagePresent) {
      getQuestions();
    } else {
      handleThankyouScreen();
    }
  };

  const onSubmitAnswers = async () => {
    try {
      setIsLoading(true);
      const payload = {
        qna_type: metaData?.qnaType,
        answers: []
      };

      for (const question of questions) {
        const questionId = question.externalId;
        const formAnswer = formData[questionId];

        const isCustomAnswerRequired = question?.options?.length === 0;

        const answerData = {
          qna_stage: question.externalId
        };

        if (isCustomAnswerRequired) {
          const customAnswer = formAnswer?.customAnswer || "";
          answerData.custom_answer =  customAnswer?.length > 0 ? customAnswer?.trim() : customAnswer;
        } else {
          if (question.multiSelect) {
            answerData.selected_options = formAnswer.answer || [];
          } else {
            answerData.selected_options = [formAnswer.answer] || [];
          }
        }
        payload.answers.push(answerData);
      }

      const apiKey = window.sessionStorage.getItem("apiKey");


      if (payload.answers.length > 0 || isOptional) {
        const response = await postOnboardingQuestions(apiKey, payload);
        if (response?.status === 201 || response?.status === 200) {
          setQuestions([]);
          setMetaData({});
          handleNextQuestions();
        }
      } else {
        triggerToast("Please enter answers", "error");
      }
      setIsLoading(false);
    } catch (error) {
      handleNewApiError(error);
      if (isOptional && !isNextStagePresent) {
        setFormData({});
      }
      setIsLoading(false);
    }
  };

  const handleThankyouScreen = async () => {
    try {
      setIsLoading(true);
      updateStep(4);
      const apiKey = window.sessionStorage.getItem("apiKey");
      const res = await getOnboardingAnswers(apiKey);

      let link = "";
      if (res && res.data) {
        for (const key in res.data) {
          const value = String(res.data[key]);
          const updatedAnswer = value.split(" ").join("_");
          link = `${link}${key}=${updatedAnswer}&`;
        }
      }
      link = link.slice(0, -1);

      if (link.length > 0) {
        router.push(`${pushUrlDomain}#register-thankyou/?${link}`);
      }
      const isExistingLead = window.sessionStorage.getItem("isExistingLead");
      if (isExistingLead === "false" && apiKey?.length > 0) {
        // Clearing Session Storage
        sessionStorage.clear();
      }
      setIsLoading(false);
    } catch (error) {
      handleNewApiError(error);
      setIsLoading(false);
    }
  };
  return (
    <OnboardingQuestions
      disableHandler={disableHandler}
      formData={formData}
      handleGoBack={handleGoBack}
      indianCityList={indianCityList}
      isLoading={isLoading}
      isUpdatingAnswer={isUpdatingAnswer}
      metaData={metaData}
      questions={questions}
      setFormData={setFormData}
      setIsUpdatingAnswer={setIsUpdatingAnswer}
      showBackButton={showBackButton}
      step={step}
      userData={userData}
      onSubmitAnswers={onSubmitAnswers}
    />
  );
};
OnboardingQuestionsContainer.propTypes = propTypes;

export default OnboardingQuestionsContainer;
